import {
  Disclaimer,
  Div,
  P,
  Separator,
  groupTaxRateAssignments,
  lightTheme,
} from '@vgrubs/components';
import {
  TaxRateAssignment,
  TaxRateAssignmentType,
} from 'components/src/types/taxRate.types';

/**
 * Where a rate that does not apply to everything is actually set.
 *
 * Built like the options under a modifier group: separated rows hanging off
 * the card above, not a card of their own.
 *
 * Grouped by brand, and by menu inside a brand - the same item copied into a
 * second menu is a separate assignment, so a flat list would show the same
 * name twice with nothing to tell them apart.
 */
export const TaxRateAssignments = ({
  assignments,
}: {
  assignments: TaxRateAssignment[];
}) => (
  <Div $p="0 0 4px">
    {assignments.length === 0 ? (
      <>
        <Separator />
        <Div $p="12px 20px" $pSM="12px 16px">
          <P $m="0" $left $color={lightTheme.darkGray}>
            Not set on anything yet. Set it on a menu item or an option, under
            Manage Menu.
          </P>
        </Div>
      </>
    ) : (
      groupTaxRateAssignments(assignments).map((group) =>
        group.menus.map((menu) => (
          <div key={`${group.brandId}-${menu.menuId ?? 'single'}`}>
            <Separator />
            <Div
              $p="10px 20px"
              $pSM="10px 16px"
              $flex
              $alignCenter
              $gap="6px"
              $wrap
            >
              <P $m="0" $left $bold>
                {group.brandName}
              </P>
              {menu.menuName && (
                <Disclaimer $m="0" $color={lightTheme.lightGray}>
                  {'·'} {menu.menuName}
                </Disclaimer>
              )}
              {group.isLocation && (
                <Disclaimer $m="0" $color={lightTheme.lightGray}>
                  {'·'} location brand
                </Disclaimer>
              )}
            </Div>

            {menu.assignments.map((assignment) => (
              <div key={`${assignment.type}-${assignment.id}`}>
                <Separator />
                <Div
                  $p="8px 20px"
                  $pSM="8px 16px"
                  $flex
                  $between
                  $alignCenter
                  $gap="16px"
                >
                  <P $m="0" $left>
                    {assignment.name}
                  </P>
                  <Disclaimer $m="0" $color={lightTheme.lightGray}>
                    {assignment.type === TaxRateAssignmentType.option
                      ? 'option'
                      : 'item'}
                  </Disclaimer>
                </Div>
              </div>
            ))}
          </div>
        ))
      )
    )}
  </Div>
);
